import cx from 'classnames';
import moment from 'moment';
import {useCallback, useContext, useMemo} from 'react';
import DayPickerInput from 'react-day-picker/DayPickerInput';
import MomentLocaleUtils from 'react-day-picker/moment';
import {useTranslation} from 'react-i18next';

import {Icon} from 'components';
import {UserContext} from 'context';
import {useLanguage} from 'hooks';
import {TRANSLATION_KEYS} from 'locale/translation-keys';
import {DateRangePickerUpdateOptions} from 'types';

import 'react-day-picker/lib/style.css';
import './styles.scss';

const T_KEYS = TRANSLATION_KEYS.USER.ACTIVITY.FORM_CONTROLS;

type Props = {
    from: moment.Moment;
    to: moment.Moment;
    className?: string;
    moveDayClassName?: string;
    onChange: (from: moment.Moment, to: moment.Moment, options?: DateRangePickerUpdateOptions) => void;
}

export const DateRangePicker = ({from: fromMoment, to: toMoment, className, moveDayClassName, onChange}: Props) => {
    const {t} = useTranslation();
    const {settings} = useContext(UserContext);
    const {timeZone, dateFormat} = useMemo(() => settings.common, [settings]);
    const [language] = useLanguage();
    const from = useMemo(() => fromMoment.toDate(), [fromMoment]);
    const to = useMemo(() => toMoment.toDate(), [toMoment]);
    const inputClassNames = useMemo(() => ({
        container: 'DayPickerInput',
        overlay: 'DayPickerInput-Overlay',
        overlayWrapper: 'DayPickerInput-OverlayWrapper -sm:tw-static'
    }), []);

    let fromElem = null;
    let toElem = null;

    const handleUpdate = useCallback((type: 'from' | 'to', date: Date) => {
        const _from = type === 'from' ? moment(date) : fromMoment.clone();
        const _to = type === 'to' ? moment(date) : toMoment.clone();
        onChange(_from, _to, {method: 'dropdown'});
    }, [fromMoment, toMoment, onChange]);

    const moveDateDays = useCallback((value: number) => {
        onChange(fromMoment.clone().add(value, 'd'), toMoment.clone().add(value, 'd'), {method: 'arrow'});
    }, [fromMoment, toMoment, onChange]);

    const formatDate = useCallback((value: Date) =>
        MomentLocaleUtils.formatDate(value, dateFormat, timeZone), [timeZone, dateFormat]);

    return (
        <div className={cx('tw-flex vs-date-picker-range', className)}>
            <div className={cx('tw-bg-light tw-flex tw-items-center tw-justify-center tw-mr-2 tw-w-8', moveDayClassName)}>
                <Icon
                    name='chevron-left'
                    button
                    onClick={() => moveDateDays(-1)}
                />
            </div>
            <div className='tw-flex tw-bg-light tw-border-b-2 tw-border-default-dimmed tw-items-center tw-relative tw-pr-2'>
                <Icon
                    name='calendar'
                    color='default-dimmed'
                    button
                    margin={false}
                    className='tw-mx-2'
                    onClick={() => fromElem.getInput().focus()}
                />
                <DayPickerInput
                    ref={(el) => fromElem = el}
                    value={from}
                    placeholder={t(T_KEYS.NAMES.FROM)}
                    formatDate={formatDate}
                    classNames={inputClassNames}
                    dayPickerProps={{
                        localeUtils: MomentLocaleUtils,
                        locale: language,
                        selectedDays: [from, {from, to}],
                        disabledDays: {after: to},
                        showOutsideDays: true,
                        toMonth: to,
                        numberOfMonths: 1,
                        onDayClick: () => toElem.getInput().focus(),
                    }}
                    onDayChange={(date) => handleUpdate('from', date)}
                />
                <Icon name='minus' />
                <DayPickerInput
                    ref={(el) => toElem = el}
                    value={to}
                    placeholder={t(T_KEYS.NAMES.TO)}
                    formatDate={formatDate}
                    classNames={inputClassNames}
                    dayPickerProps={{
                        localeUtils: MomentLocaleUtils,
                        locale: language,
                        selectedDays: [from, {from, to}],
                        disabledDays: {before: from},
                        showOutsideDays: true,
                        month: from,
                        fromMonth: from,
                        numberOfMonths: 1,
                    }}
                    onDayChange={(date) => handleUpdate('to', date)}
                />
            </div>
            <div className={cx('tw-bg-light tw-flex tw-items-center tw-justify-center tw-ml-2 tw-w-8', moveDayClassName)}>
                <Icon
                    name='chevron-right'
                    button
                    onClick={() => moveDateDays(1)}
                />
            </div>
        </div>
    );
};